import React from "react";
import { motion } from "framer-motion";
import environment from "../../assets/logo/environment.svg";
import handshakeIcon from "../../assets/logo/handshake.svg";
import price from "../../assets/logo/price.svg";
import team from "../../assets/logo/team.svg";
import experience from "../../assets/logo/experience.svg";
import quality from "../../assets/logo/quality.svg";
import layanan from "../../assets/about7.jpeg";

const data = [
  {
    title: "Pengalaman dan Keahlian",
    text: "Berpengalaman pengelolaan limbah cair di berbagai industri.",
    imageUrl: experience,
    color: "from-brand-500 to-brand-600",
  },
  {
    title: "Pelayanan Terpadu",
    text: "Mengelola perizinan lingkungan, konsultasi dan dukungan teknis yang tepat.",
    imageUrl: handshakeIcon,
    color: "from-eco-500 to-eco-600",
  },
  {
    title: "Komitmen terhadap Kualitas",
    text: "Standar tinggi dan pengawasan ketat untuk hasil terbaik.",
    imageUrl: quality,
    color: "from-brand-600 to-eco-500",
  },
  {
    title: "Keberlanjutan Lingkungan",
    text: "Berfokus pada solusi berkelanjutan pelestarian lingkungan.",
    imageUrl: environment,
    color: "from-eco-500 to-brand-600",
  },
  {
    title: "Tim yang Berdedikasi",
    text: "Tim profesional, berdedikasi dan komitmen dalam pelayanan terbaik.",
    imageUrl: team,
    color: "from-brand-500 to-eco-500",
  },
  {
    title: "Harga Kompetitif",
    text: "Penawaran dengan harga bersaing dan transparan.",
    imageUrl: price,
    color: "from-eco-600 to-brand-500",
  },
];

const WhyChooseUs = () => {
  return (
    <section
      id="OurAdvantages"
      className="relative py-20 md:py-28 bg-white overflow-hidden"
    >
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-brand-100/40 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-eco-100/40 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="w-full lg:w-5/12 lg:sticky lg:top-28"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-strong">
              <img
                src={layanan}
                alt="Layanan Pengolahan Limbah"
                loading="lazy"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-900/20 to-transparent" />

              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-strong border border-white/40"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-500 to-eco-500 flex items-center justify-center text-white shadow-lg">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-oswald text-lg font-semibold text-slate-900">
                      Tersertifikasi
                    </div>
                    <div className="text-xs text-slate-600 font-overpass">
                      Standar lingkungan terpenuhi
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Text Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="w-full lg:w-7/12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-brand-50 text-brand-700 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
              Kenapa Memilih Kami
            </span>

            <h2 className="font-oswald text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 leading-tight mb-5">
              Layanan Pengolahan Limbah Cair{" "}
              <span className="text-gradient">Profesional</span>
            </h2>

            <p className="font-overpass text-base lg:text-lg text-slate-600 leading-relaxed mb-8">
              Kami menyediakan solusi lengkap dalam pengolahan limbah cair,
              mulai dari desain instalasi, pengelolaan operasional, hingga
              pemantauan kualitas air. Dukung bisnis Anda dengan layanan
              terpercaya yang sesuai standar lingkungan dan regulasi pemerintah.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {data.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  className="group relative p-5 rounded-2xl bg-white border border-slate-100 shadow-soft hover:shadow-medium card-hover overflow-hidden"
                >
                  <div
                    className={`absolute -top-10 -right-10 w-24 h-24 rounded-full bg-gradient-to-br ${item.color} opacity-10 group-hover:opacity-20 transition-opacity blur-2xl`}
                  />
                  <div className="relative flex items-start gap-4">
                    <div
                      className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
                    >
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="w-7 h-7 filter brightness-0 invert"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-oswald font-semibold text-base text-slate-900 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-sm text-slate-600 font-overpass leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
